import { useMemo } from 'react';
import { FormHelperText, Stack } from '@mui/material';

import { FWeeklySchedule } from '../../../../../components/baseV2';
import { ConfirmDialog, useFormReadOnly } from '../../../components';
import { useScheduleDeleteDialog, useScheduleSlotForm } from './hooks';
import {
  getScheduleValidationErrors,
  toChildSchedulePayload,
  toWeeklyScheduleItems,
} from '../../utils';
import { FORTA_ERROR } from '../../../../../utils/colors';
import ScheduleActivityLegend from '../ScheduleActivityLegend';
import ScheduleSlotFields from '../ScheduleSlotFields';

import type { ClientScheduleSectionProps } from '../types';

export default function ClientScheduleSection({
  schedules,
  notAttendingSchool,
  isSaving,
  disabled,
  showScheduleErrors,
  onAdd,
  onDelete,
}: ClientScheduleSectionProps) {
  const readOnly = useFormReadOnly();
  const scheduleItems = useMemo(() => toWeeklyScheduleItems(schedules), [schedules]);
  const scheduleErrors = useMemo(
    () => getScheduleValidationErrors(schedules, notAttendingSchool),
    [schedules, notAttendingSchool],
  );
  const slotForm = useScheduleSlotForm(schedules);
  const deleteDialog = useScheduleDeleteDialog({ onDelete });

  const addTimeSlot = async () => {
    const { slot, reset } = slotForm;
    if (!slot) {
      return;
    }
    if (await onAdd(toChildSchedulePayload(slot))) {
      reset();
    }
  };

  return (
    <>
      {readOnly ? null : (
        <ScheduleSlotFields
          activityType={slotForm.activityType}
          canAdd={slotForm.slot !== null}
          dayOfWeek={slotForm.dayOfWeek}
          disabled={disabled || readOnly}
          endHour={slotForm.endHour}
          endTimeOptions={slotForm.endTimeOptions}
          isSaving={isSaving}
          onActivityChange={slotForm.handleActivityChange}
          onAdd={addTimeSlot}
          onDayChange={slotForm.handleDayChange}
          onEndTimeChange={slotForm.handleEndTimeChange}
          onStartTimeChange={slotForm.handleStartTimeChange}
          startHour={slotForm.startHour}
          startTimeOptions={slotForm.startTimeOptions}
        />
      )}
      <ScheduleActivityLegend />
      {showScheduleErrors && scheduleErrors.length > 0 ? (
        <Stack>
          {scheduleErrors.map((error) => (
            <FormHelperText sx={{ color: FORTA_ERROR, fontWeight: 600 }} key={error}>
              {error}
            </FormHelperText>
          ))}
        </Stack>
      ) : null}
      <FWeeklySchedule
        values={scheduleItems}
        onDelete={readOnly ? undefined : deleteDialog.requestDeleteSchedule}
      />
      <ConfirmDialog
        open={deleteDialog.isDeleteDialogOpen}
        title="Delete activity"
        message="Are you sure you want to delete this activity? This action cannot be undone."
        onConfirm={deleteDialog.confirmDeleteSchedule}
        onClose={deleteDialog.cancelDeleteSchedule}
        confirmLoading={isSaving}
      />
    </>
  );
}
